import { c as _c } from "react/compiler-runtime";
import { BasicTooltip } from '@/comps/basic-tooltip';
import { Chip } from '@/comps/chip';
import { Separator } from '@/comps/primitives/separator';
import { XCircleIcon, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
import { getFilterTooltip } from '@/utils/filter-tooltips';
import React, { useCallback } from 'react';
import { Button } from '../primitives/button';
import { UISelect, UISelectOption } from '@/comps/ui/UISelect';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { WithClassName, WithDisabled } from '@/types/component';
import { cn } from '@/utils/shad';
export type IsValue = 'is' | 'is_not'; // future values will be "is set", "is not set", "contains", "does not contain"

type Props = {
  title?: string | React.ReactNode;
  text: string;
  isExclude?: boolean;
  selectAll?: boolean;
  onClickClose: () => void;
  onChangeIsValue?: (value: IsValue) => void;
  filterKind?: string;
  /** Renders a non-interactive "is" label (e.g. Group is …) instead of the is / is_not selector */
  readOnlyIs?: boolean;
  showRemoveButton?: boolean;
};
export function FilterKindValue(t0) {
  const $ = _c(23);
  const {
    text,
    title,
    isExclude,
    selectAll,
    onClickClose,
    onChangeIsValue,
    filterKind,
    readOnlyIs,
    showRemoveButton: t1
  } = t0;
  const showRemoveButton = t1 === undefined ? true : t1;
  const isValue = isExclude ? "is_not" : "is";
  let t2;
  if ($[0] !== filterKind) {
    t2 = filterKind ? getFilterTooltip(filterKind) : undefined;
    $[0] = filterKind;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const tooltipText = t2;
  let t3;
  if ($[2] !== isExclude || $[3] !== isValue || $[4] !== onChangeIsValue || $[5] !== readOnlyIs || $[6] !== title || $[7] !== tooltipText) {
    t3 = !!title && <><div className="flex items-center gap-1"><p className="text-darkgrey">{title}</p>{tooltipText && <Tooltip delayDuration={300}><TooltipTrigger><Info size={10} className="cursor-pointer text-gray-500 hover:text-white" /></TooltipTrigger><TooltipContent>{tooltipText}</TooltipContent></Tooltip>}</div>{readOnlyIs ? <><Separator orientation="vertical" className="mt-0.5 ml-1.5 h-2 w-px bg-darkgrey" /><span className="mx-1 font-semibold text-darkgrey">is</span><Separator orientation="vertical" className="mt-0.5 mr-1.5 h-2 w-px bg-darkgrey" /></> : onChangeIsValue ? <><Separator orientation="vertical" className="mt-0.5 ml-1.5 h-2 w-px bg-darkgrey" /><div onClick={_temp}><IsSelector value={isValue} onChange={onChangeIsValue} /></div><Separator orientation="vertical" className="mt-0.5 mr-1.5 h-2 w-px bg-darkgrey" /></> : <>{isExclude && <span className="mx-1 text-darkgrey">is not </span>}<Separator orientation="vertical" className="mx-1.5 mt-0.5 h-2 w-px bg-darkgrey" /></>}</>;
    $[2] = isExclude;
    $[3] = isValue;
    $[4] = onChangeIsValue;
    $[5] = readOnlyIs;
    $[6] = title;
    $[7] = tooltipText;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = selectAll ? text + " + ALL" : text;
  const t5 = selectAll ? text + " + ALL" : text;
  let t6;
  if ($[9] !== t5) {
    t6 = <p className="truncate text-muted-foreground">{t5}</p>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <BasicTooltip text={t4}>{t6}</BasicTooltip>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t3 || $[15] !== t7) {
    t8 = <Chip size="small" className="truncate rounded-[8px] bg-background px-2" variant="secondary"><div className="flex max-w-50 items-center">{t3}{t7}</div></Chip>;
    $[14] = t3;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== onClickClose || $[18] !== showRemoveButton) {
    t9 = showRemoveButton && <Button aria-label="Remove filter kind" className="h-3 w-3 text-darkgrey" variant="ghost" size="icon" onClick={onClickClose}><XCircleIcon size={13} /></Button>;
    $[17] = onClickClose;
    $[18] = showRemoveButton;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = <div className="flex items-center gap-1 text-[12px]">{t8}{t9}</div>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp(e) {
  return e.stopPropagation();
}
type IsSelectorProps = WithClassName & WithDisabled & {
  value: IsValue;
  onChange: (value: IsValue) => void;
};
export function IsSelector(t0) {
  const $ = _c(9);
  const {
    value,
    onChange,
    className,
    disabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      value: "is",
      label: "is"
    }, {
      value: "is_not",
      label: "is not"
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const options = t1;
  let t2;
  if ($[1] !== className) {
    t2 = cn("border-none p-0 text-[12px] font-semibold hover:underline focus:ring-0", className);
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <></>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== disabled || $[5] !== onChange || $[6] !== t2 || $[7] !== value) {
    t4 = <UISelect disabled={disabled} className={t2} value={value} options={options} onChange={onChange} caretIcon={t3} />;
    $[4] = disabled;
    $[5] = onChange;
    $[6] = t2;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
export function useChangeIsValueHandler(filters, setFilters) {
  const $ = _c(5);
  let t0;
  if ($[0] !== filters || $[1] !== setFilters) {
    t0 = (isValue, kind) => {
      const newFilters = [...filters];
      const foundFilterIndex = newFilters.findIndex(f => f.column === kind);
      if (foundFilterIndex !== -1) {
        newFilters[foundFilterIndex].is_exclude = isValue !== "is";
      }
      setFilters(newFilters);
    };
    $[0] = filters;
    $[1] = setFilters;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleChangeIsValue = t0;
  let t1;
  if ($[3] !== handleChangeIsValue) {
    t1 = {
      handleChangeIsValue
    };
    $[3] = handleChangeIsValue;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}